const API_PREFIX = '/api';

let _lastCredentials = null;
let _reconnecting = null;

const parseApiError = async (response) => {
  try {
    const data = await response.json();
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  } catch {
    // Ignore parse failure and use fallback.
  }
  return `${response.status} ${response.statusText}`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getRetryDelay = (response) => {
  const retryAfter = response.headers.get('Retry-After');
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (!isNaN(seconds) && seconds > 0) return seconds * 1000;
  }
  return 5000;
};

const autoReconnect = async () => {
  if (!_lastCredentials) return false;
  if (_reconnecting) return _reconnecting;
  _reconnecting = (async () => {
    try {
      console.log('[webClient] auto-reconnecting...');
      const res = await fetch(`${API_PREFIX}/session/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(_lastCredentials)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok) {
          console.log('[webClient] auto-reconnect succeeded');
          return true;
        }
      }
      console.warn('[webClient] auto-reconnect failed');
      return false;
    } catch (err) {
      console.warn('[webClient] auto-reconnect error:', err.message);
      return false;
    } finally {
      _reconnecting = null;
    }
  })();
  return _reconnecting;
};

const requestJson = async (path, options = {}, retries = 3, _didReconnect = false) => {
  const response = await fetch(`${API_PREFIX}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });

  if (response.status === 429 && retries > 0) {
    const delay = getRetryDelay(response);
    await sleep(delay);
    return requestJson(path, options, retries - 1, _didReconnect);
  }

  if (response.status === 401 && !_didReconnect && !path.startsWith('/session/')) {
    const ok = await autoReconnect();
    if (ok) return requestJson(path, options, retries, true);
  }

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  if (response.status === 204) return null;
  return response.json();
};

const requestBlob = async (path, retries = 3, _didReconnect = false) => {
  const response = await fetch(`${API_PREFIX}${path}`, {
    credentials: 'include'
  });
  if (response.status === 429 && retries > 0) {
    const delay = getRetryDelay(response);
    await sleep(delay);
    return requestBlob(path, retries - 1, _didReconnect);
  }
  if (response.status === 401 && !_didReconnect) {
    const ok = await autoReconnect();
    if (ok) return requestBlob(path, retries, true);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }
  return response.blob();
};

export const webClient = {
  async getSessionStatus() {
    return requestJson('/session/status');
  },

  async connect(config, connectionId = 'default') {
    const payload = { ...config, connectionId };
    const result = await requestJson('/session/connect', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (result?.ok) {
      _lastCredentials = payload;
    }
    return result;
  },

  async disconnect(connectionId = null) {
    _lastCredentials = null;
    await requestJson('/session/disconnect', {
      method: 'POST',
      body: JSON.stringify(connectionId ? { connectionId } : {})
    });
  },

  async setActiveConnection(connectionId) {
    await requestJson('/session/setActive', {
      method: 'POST',
      body: JSON.stringify({ connectionId })
    });
  },

  async listRemote(remotePath = '/') {
    return requestJson(`/remote/list?path=${encodeURIComponent(remotePath)}`);
  },

  async readRemoteFile(remotePath) {
    const result = await requestJson(`/remote/file?path=${encodeURIComponent(remotePath)}`);
    return result.content || '';
  },

  async writeRemoteFile(remotePath, content) {
    await requestJson('/remote/file', {
      method: 'PUT',
      body: JSON.stringify({ path: remotePath, content })
    });
  },

  async createRemoteFile(remotePath, content = '') {
    await requestJson('/remote/file', {
      method: 'POST',
      body: JSON.stringify({ path: remotePath, content })
    });
  },

  async createRemoteDir(remotePath) {
    await requestJson('/remote/dir', {
      method: 'POST',
      body: JSON.stringify({ path: remotePath })
    });
  },

  async renameRemote(from, to) {
    await requestJson('/remote/rename', {
      method: 'POST',
      body: JSON.stringify({ from, to })
    });
  },

  async deleteRemote(remotePath) {
    await requestJson(`/remote/item?path=${encodeURIComponent(remotePath)}`, {
      method: 'DELETE'
    });
  },

  uploadFile(remotePath, file, { onProgress, signal } = {}) {
    const body = file instanceof Blob ? file : new Blob([file]);
    const attempt = (retriesLeft, didReconnect = false) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_PREFIX}/remote/upload?path=${encodeURIComponent(remotePath)}`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.withCredentials = true;

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else if (xhr.status === 429 && retriesLeft > 0) {
          const retryAfter = xhr.getResponseHeader('Retry-After');
          const delay = (retryAfter && !isNaN(Number(retryAfter))) ? Number(retryAfter) * 1000 : 5000;
          sleep(delay).then(() => attempt(retriesLeft - 1, didReconnect).then(resolve, reject));
        } else if (xhr.status === 401 && !didReconnect) {
          autoReconnect().then((ok) => {
            if (ok) attempt(retriesLeft, true).then(resolve, reject);
            else reject(new Error('Session expired. Please reconnect.'));
          });
        } else {
          let msg = `${xhr.status} ${xhr.statusText}`;
          try {
            const data = JSON.parse(xhr.responseText);
            if (data?.error) msg = data.error;
          } catch { /* ignore */ }
          reject(new Error(msg));
        }
      };

      xhr.onerror = () => {
        if (retriesLeft > 0) {
          sleep(2000).then(() => attempt(retriesLeft - 1, didReconnect).then(resolve, reject));
        } else {
          reject(new Error('Upload network error'));
        }
      };
      xhr.onabort = () => reject(new Error('Upload cancelled'));

      if (signal) {
        if (signal.aborted) {
          xhr.abort();
          return;
        }
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }

      xhr.send(body);
    });
    return attempt(3);
  },

  async downloadFile(remotePath) {
    return requestBlob(`/remote/download?path=${encodeURIComponent(remotePath)}`);
  },

  async transferConnect(side, config) {
    return requestJson('/transfer/connect', {
      method: 'POST',
      body: JSON.stringify({ ...config, side })
    });
  },

  async transferList(side, remotePath = '/') {
    return requestJson(`/transfer/list?side=${encodeURIComponent(side)}&path=${encodeURIComponent(remotePath)}`);
  },

  async transferCopy({ from, to, paths, targetDir, onProgress }) {
    await requestJson('/transfer/copy', {
      method: 'POST',
      body: JSON.stringify({ from, to, paths, targetDir })
    });
    // Poll for progress
    let cursor = 0;
    while (true) {
      await sleep(800);
      const res = await requestJson(`/transfer/progress?cursor=${cursor}`);
      if (res.events && res.events.length > 0) {
        for (const event of res.events) {
          if (onProgress) onProgress(event);
        }
      }
      cursor = res.cursor || cursor;
      if (res.done) {
        if (res.error) throw new Error(res.error);
        break;
      }
    }
    return { ok: true };
  }
};
